import httpService from '@/modules/common/services/httpService';
import gatewayService from '@/modules/common/services/gatewayService';

const V3_APPS_ROUTE = 'v3/apps';
const V3_INSTALLS_ROUTE = `${V3_APPS_ROUTE}/business_app_installs`;

const getExtensionValue = (extensions, type) => {
  const ext = extensions?.find((e) => e.type === type);
  return ext?.value ?? null;
};

const mapV3AppToLegacy = (v3App) => {
  const extensions = v3App.extensions || [];

  return {
    app_id: v3App.uid,
    app_name: v3App.name,
    app_code_name: v3App.app_code_name,
    app_host: v3App.app_host,
    api_uri: v3App.api_uri ?? v3App.internal?.api_uri,
    app_types: v3App.app_types || [],
    description: v3App.app_market?.description || {},
    app_screenshot_uris: v3App.app_market?.app_screenshot_uris || [],
    app_mobile_screenshot_uris: v3App.app_market?.app_mobile_screenshot_uris || [],
    app_features: v3App.app_market?.app_features || [],
    supported_countries: v3App.app_market?.supported_countries || [],
    locales: v3App.app_market?.locales || [],
    privacy_policy_link: v3App.app_market?.privacy_policy_link,
    contact_support_link: v3App.app_market?.contact_support_link,
    personal_connection: v3App.app_market?.personal_connection,
    url_params: v3App.url_params || [],
    permissions: v3App.permissions || [],
    open_in_new_tab: v3App.open_in_new_tab || false,
    supported_in_mobile: v3App.supported_in_mobile,
    status: v3App.assignment_types?.includes('installed') ? 'installed' : 'not_installed',
    requires_upgrade: v3App.app_market?.requires_upgrade || false,
    requires_upgrade_if_paid: v3App.app_market?.requires_upgrade_if_paid || false,
    assignment_types: v3App.assignment_types || [],
    has_offering: v3App.assignment_types?.includes('directory_offering') || false,
    old_integration_name: getExtensionValue(extensions, 'old_integration_name'),
    menu_items: getExtensionValue(extensions, 'menu_items'),
    deep_link: v3App.internal?.deep_link,
    full_screen: v3App.internal?.full_screen || false,
    additional_params: getExtensionValue(extensions, 'additional_params'),
  };
};

const APPS_PER_PAGE = 100;

// The catalog gates CRM page render via the Angular appsReady route resolve, and the
// fetch can fail transiently on a cold page load (gateway auth not ready yet). Retry
// with backoff so a transient failure doesn't leave the store unloaded (VCITA2-14215).
const FETCH_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;
const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const fetchAppsV3 = async (attempt = 0) => {
  try {
    return await gatewayService.get(`${V3_APPS_ROUTE}/apps?per_page=${APPS_PER_PAGE}`);
  } catch (error) {
    if (attempt >= FETCH_RETRIES) throw error;
    await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
    return fetchAppsV3(attempt + 1);
  }
};

const getAppsV3 = async () => {
  const res = await fetchAppsV3();
  const v3Apps = res?.data?.data?.apps || [];

  if (v3Apps.length >= APPS_PER_PAGE) {
    console.warn(`getAppsV3: received a full page (${APPS_PER_PAGE}) — catalog may be truncated (pagination not implemented)`);
  }

  return v3Apps.map(mapV3AppToLegacy);
};

const installApp = async (params) => {
  let result;
  try {
    const res = await gatewayService.post(V3_INSTALLS_ROUTE, {
      business_uid: params.business_uid,
      app_code_name: params.app_code_name,
    });
    result = res.data?.data;
  } catch (error) {
    result = { code: 'error' };
    console.error(error);
  }
  return result;
};

const uninstallApp = async (params) => {
  let result;
  try {
    const res = await gatewayService.get(V3_INSTALLS_ROUTE, {
      business_uid: params.business_uid,
      app_code_name: params.app_code_name,
    });
    const installs = res.data?.data?.business_app_installs || [];
    const install = installs[0];

    if (install) {
      await gatewayService.remove(`${V3_INSTALLS_ROUTE}/${install.uid}`);
    }

    result = { app_code_name: params.app_code_name };
  } catch (error) {
    result = { code: 'error' };
    console.error(error);
  }
  return result;
};

const logUpgradeButtonClicked = async (appCodeName) => {
  let result;
  try {
    await httpService.post('platform/v1/apps/manager/upgrade_clicked_log', { app_code_name: appCodeName });
  } catch (error) {
    result = { code: 'error' };
    console.error(error);
  }
  return result;
};

const appsWithBetaBadge = ['esignatures', 'tips'];

export {
  getAppsV3,
  installApp,
  uninstallApp,
  appsWithBetaBadge,
  logUpgradeButtonClicked,
};
